import React, { useEffect, useState } from 'react';
import { Alert, Box, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, Button, Stack, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import FormularioVentaForm, { FormularioVentaFormDocument } from '../../components/FormularioVentaForm';
import ClienteSearch from '../../components/Tesoreria/ClienteSearch';
import remitoVentaService, { RemitoVentaCreateDto } from '../../services/remitoVentaService';
import {
  VentaPendienteMaster,
  cantidadSugeridaDesdePendienteVenta,
} from '../../services/ventasSeguimientoService';
import {
  advertirLineasConPrecioCero,
  precioListaDesdePendienteMaster,
} from '../../utils/pedidoVentaLineas';
import { getApiErrorMessage } from '../../utils/apiError';

const getToday = () => new Date();

const normalizarFormaDePago = (valor?: string | null): string => {
  if (!valor) return '';
  const clean = String(valor).trim().toUpperCase();
  const formas = ['CONTADO', 'CTA.CTE.', 'EFECTIVO', 'DEVOLUCION', 'CONTRAREEMBOLSO'];
  return formas.find((f) => f.toUpperCase() === clean) ?? '';
};

const obtenerNroUbicacionDesdeTexto = (ubicacion?: string): number | undefined => {
  if (!ubicacion) return undefined;
  const match = ubicacion.match(/\((\d+)\)\s*$/);
  if (match?.[1]) return Number(match[1]);
  const soloNumero = Number(ubicacion.trim());
  return Number.isFinite(soloNumero) && soloNumero > 0 ? soloNumero : undefined;
};

const RemitoVentaForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clienteDialogOpen, setClienteDialogOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [nroRemitoCreado, setNroRemitoCreado] = useState<number | null>(null);
  const [pendingApplied, setPendingApplied] = useState(false);
  const round2 = (n: number) => Math.round(n * 100) / 100;
  const navState = (location.state as {
    ventaPendiente?: VentaPendienteMaster;
    remitoParcialDesdeFactura?: {
      nroFormularioFactura: number;
      formulario: {
        nroCliente: number;
        nombre?: string;
        cuit?: string;
        nroLista?: string;
        nroSuc: number;
        descripcion?: string;
        nroUbicacionOrigen?: number;
        porcDesc?: number | null;
        porcDesc2?: number | null;
        porcDesc3?: number | null;
      };
      items: Array<{
        nroFormularioMaster?: number;
        nroItemMaster?: number;
        codigoArticulo: string;
        descripcionArticulo?: string;
        cantidad: number;
        precioUnitario: number;
        porcDesc?: number;
        notas?: string;
      }>;
    };
  } | null);
  const ventaPendiente = navState?.ventaPendiente;
  const remitoParcialDesdeFactura = navState?.remitoParcialDesdeFactura;

  const [documento, setDocumento] = useState<FormularioVentaFormDocument>({
    nroFormulario: 0,
    nroTipoFormulario: '9',
    tipoCliente: 'C',
    fechaEmision: getToday(),
    horaEmision: getToday(),
    nroCliente: 0,
    nombre: '',
    nroVendedor: 0,
    nroLista: '',
    codigoMoneda: 'ARS',
    importeTotal: 0,
    asentado: 'N',
    descripcion: '',
    usuario: '',
    ordenCompra: '',
    transporte: '',
    ubicacion: '',
    formaDePago: '',
    nroSuc: 0,
    cuit: '',
    dni: '',
    tipoIVA: '',
    idCompania: '',
    detalles: [],
    porcDesc: 0,
    porcDesc2: 0,
    porcDesc3: 0,
  });

  useEffect(() => {
    if (pendingApplied || !ventaPendiente || remitoParcialDesdeFactura) {
      return;
    }

    setDocumento((prev) => {
      const alreadyIncluded = (prev.detalles || []).some(
        (d) =>
          d.nroFormularioMaster === ventaPendiente.nroFormularioMaster &&
          d.nroItemMaster === ventaPendiente.nroItemMaster
      );
      if (alreadyIncluded) return prev;

      const cantidadPendienteEntregar = Number(ventaPendiente.pendienteEntregar) || 0;
      const cantidad =
        cantidadPendienteEntregar > 0
          ? cantidadPendienteEntregar
          : cantidadSugeridaDesdePendienteVenta(ventaPendiente);
      const precioLista = precioListaDesdePendienteMaster(ventaPendiente);
      const porcDesc = Number(ventaPendiente.porcDesc1Master ?? 0) || 0;
      const precioConDescuento = precioLista - (precioLista * porcDesc) / 100;
      const subtotal = round2(cantidad * precioConDescuento);

      return {
        ...prev,
        nroCliente:
          ventaPendiente.nroCliente && ventaPendiente.nroCliente > 0
            ? ventaPendiente.nroCliente
            : prev.nroCliente,
        nombre: ventaPendiente.nombreCliente || prev.nombre,
        detalles: [
          ...(prev.detalles || []),
          {
            nroIdformulario: prev.nroFormulario,
            nroItem: (prev.detalles?.length || 0) + 1,
            nroFormularioMaster: ventaPendiente.nroFormularioMaster,
            nroItemMaster: ventaPendiente.nroItemMaster,
            codigoArticulo: ventaPendiente.codigoArticulo || '',
            descripcionArticulo: ventaPendiente.descripcionArticulo || '',
            cantidad,
            precioUnitario: precioLista,
            descuento: 0,
            porcDesc,
            subtotal,
          },
        ],
        importeTotal: round2((prev.importeTotal || 0) + subtotal),
      };
    });

    setPendingApplied(true);
  }, [pendingApplied, remitoParcialDesdeFactura, ventaPendiente]);

  useEffect(() => {
    if (pendingApplied || !remitoParcialDesdeFactura) {
      return;
    }

    setDocumento((prev) => {
      const detalles = remitoParcialDesdeFactura.items.map((item, index) => {
        const porcDesc = Number(item.porcDesc ?? 0) || 0;
        const precioLista = Number(item.precioUnitario ?? 0);
        const precioConDescuento = precioLista - (precioLista * porcDesc) / 100;
        const cantidad = Number(item.cantidad ?? 0) || 0;
        const subtotal = round2(cantidad * precioConDescuento);
        return {
          nroIdformulario: prev.nroFormulario,
          nroItem: index + 1,
          nroFormularioMaster: item.nroFormularioMaster,
          nroItemMaster: item.nroItemMaster,
          codigoArticulo: item.codigoArticulo || '',
          descripcionArticulo: item.descripcionArticulo || '',
          cantidad,
          precioUnitario: precioLista,
          descuento: 0,
          porcDesc,
          subtotal,
          notas: item.notas || '',
        };
      });

      const total = round2(detalles.reduce((acc, d) => acc + Number(d.subtotal ?? 0), 0));
      return {
        ...prev,
        nroCliente: Number(remitoParcialDesdeFactura.formulario.nroCliente ?? prev.nroCliente ?? 0),
        nombre: remitoParcialDesdeFactura.formulario.nombre ?? prev.nombre,
        cuit: remitoParcialDesdeFactura.formulario.cuit ?? prev.cuit,
        nroLista: remitoParcialDesdeFactura.formulario.nroLista ?? prev.nroLista,
        nroSuc: Number(remitoParcialDesdeFactura.formulario.nroSuc ?? prev.nroSuc ?? 0),
        descripcion: remitoParcialDesdeFactura.formulario.descripcion ?? prev.descripcion,
        porcDesc: Number(remitoParcialDesdeFactura.formulario.porcDesc ?? prev.porcDesc ?? 0),
        porcDesc2: Number(remitoParcialDesdeFactura.formulario.porcDesc2 ?? prev.porcDesc2 ?? 0),
        porcDesc3: Number(remitoParcialDesdeFactura.formulario.porcDesc3 ?? prev.porcDesc3 ?? 0),
        ubicacion:
          remitoParcialDesdeFactura.formulario.nroUbicacionOrigen &&
          remitoParcialDesdeFactura.formulario.nroUbicacionOrigen > 0
            ? String(remitoParcialDesdeFactura.formulario.nroUbicacionOrigen)
            : prev.ubicacion,
        detalles,
        importeTotal: total,
      };
    });

    setPendingApplied(true);
  }, [pendingApplied, remitoParcialDesdeFactura]);

  const handleClienteSelect = (cliente: any) => {
    setDocumento((prev) => ({
      ...prev,
      nroCliente: Number(cliente.nroCliente ?? cliente.codigo ?? 0),
      nombre: cliente.nombre ?? '',
      cuit: cliente.cuit ?? '',
      dni: cliente.dni ?? '',
      tipoIVA: cliente.tipoIva ?? '',
      nroVendedor: Number(cliente.vendedor) > 0 ? Number(cliente.vendedor) : prev.nroVendedor,
      formaDePago: normalizarFormaDePago(cliente.condVta),
      transporte:
        cliente.nroExpreso !== undefined && cliente.nroExpreso !== null && String(cliente.nroExpreso).trim() !== ''
          ? String(cliente.nroExpreso)
          : prev.transporte,
      porcDesc: Number(cliente.porcDesc ?? cliente.porc_Desc ?? cliente.porcdesc ?? 0) || 0,
      porcDesc2: Number(cliente.porcDesc2 ?? cliente.porc_Desc2 ?? cliente.porcdesc2 ?? 0) || 0,
      porcDesc3: Number(cliente.porcDesc3 ?? cliente.porc_Desc3 ?? cliente.porcdesc3 ?? 0) || 0,
      nroLista: (cliente.nroLista ?? '').toString().trim() || prev.nroLista,
    }));
    setClienteDialogOpen(false);
  };

  const handleSave = async () => {
    setError(null);

    if (!documento.nroCliente || documento.nroCliente <= 0) {
      enqueueSnackbar('Debe seleccionar un cliente', { variant: 'warning' });
      return;
    }
    if (!documento.detalles || documento.detalles.length === 0) {
      enqueueSnackbar('Debe agregar al menos un artículo', { variant: 'warning' });
      return;
    }
    if (!documento.nroSuc || documento.nroSuc <= 0) {
      enqueueSnackbar('Debe seleccionar una sucursal válida', { variant: 'warning' });
      return;
    }

    const itemsInvalidos = documento.detalles.filter((d) => {
      if (!d.codigoArticulo || Number(d.cantidad ?? 0) <= 0) return true;
      const precio = Number(d.precioUnitario);
      return d.precioUnitario == null || !Number.isFinite(precio) || precio < 0;
    });
    if (itemsInvalidos.length > 0) {
      enqueueSnackbar('Hay artículos con código o cantidad inválidos', { variant: 'error' });
      return;
    }

    advertirLineasConPrecioCero(
      documento.detalles.map((d) => ({
        precioUnitario: d.precioUnitario,
        descuento: d.descuento,
        porcDesc: d.porcDesc ?? d.descuento,
        codigoArticulo: d.codigoArticulo,
        descripcionArticulo: d.descripcionArticulo,
      })),
      enqueueSnackbar,
      'remito',
    );

    const payload: RemitoVentaCreateDto = {
      formulario: {
        descripcion: documento.descripcion || undefined,
        nroSuc: documento.nroSuc || undefined,
        fechaEmision: documento.fechaEmision ? new Date(documento.fechaEmision) : new Date(),
        horaEmision: documento.horaEmision ? new Date(documento.horaEmision) : new Date(),
        fechaVencim: documento.fechaEmision ? new Date(documento.fechaEmision) : new Date(),
        nroCliente: documento.nroCliente,
        nombre: documento.nombre || undefined,
        cuit: documento.cuit || undefined,
        nroLista: documento.nroLista || undefined,
        nroUbicacionOrigen: obtenerNroUbicacionDesdeTexto(documento.ubicacion),
        formaEntrega: documento.transporte || undefined,
        porcDesc: documento.porcDesc || 0,
        porcDesc2: documento.porcDesc2 || 0,
        porcDesc3: documento.porcDesc3 || 0,
        nroFormularioFactura: remitoParcialDesdeFactura?.nroFormularioFactura,
      },
      items: documento.detalles.map((d) => ({
        codigoArticulo: String(d.codigoArticulo ?? '').trim(),
        descripcion: d.descripcionArticulo || undefined,
        cantidad: Number(d.cantidad ?? 0),
        precioUnitario: Number(d.precioUnitario ?? 0),
        porcDesc: Number(d.descuento ?? d.porcDesc ?? 0),
        nroFormularioMaster: d.nroFormularioMaster,
        nroItemMaster: d.nroItemMaster,
        notas: d.notas || undefined,
        um: d.um || undefined,
        codigoMoneda: d.codigoMoneda,
        cotizacion: d.cotizacion,
      })),
    };

    try {
      setLoading(true);
      const creado = await remitoVentaService.createRemitoVenta(payload);
      setNroRemitoCreado(creado.nroFormulario);
      setSuccessDialogOpen(true);
      enqueueSnackbar(`Remito #${creado.nroFormulario} creado correctamente`, { variant: 'success' });
    } catch (err: unknown) {
      const msg = getApiErrorMessage(err, 'Error al crear el remito');
      setError(msg);
      enqueueSnackbar(msg, { variant: 'error', autoHideDuration: 6000 });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSuccess = () => {
    setSuccessDialogOpen(false);
    navigate('/ventas/remitos');
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Nuevo Remito de Venta
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/ventas/remitos'))}
            >
              Volver
            </Button>
          </Stack>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <FormularioVentaForm
            document={documento}
            onChange={setDocumento}
            onClienteSearch={() => setClienteDialogOpen(true)}
            onProductoSearch={() => {}}
            onSave={handleSave}
            loading={loading}
            saveButtonText="Guardar Remito"
            tabLabelInfo="Información del Remito"
            tabLabelResumen="Resumen del Remito"
            ariaLabelTabs="pestañas de remito"
            hideFinancials
            permitirPrecioCero
          />
        </CardContent>
      </Card>

      <ClienteSearch
        open={clienteDialogOpen}
        onClose={() => setClienteDialogOpen(false)}
        onSelect={handleClienteSelect}
      />

      <Dialog open={successDialogOpen} onClose={handleCloseSuccess} maxWidth="xs" fullWidth>
        <DialogTitle>Remito creado</DialogTitle>
        <DialogContent>
          <Typography>Remito creado exitosamente. Número: {nroRemitoCreado}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSuccess}>OK</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RemitoVentaForm;
